import React, { useState } from 'react';
import { PatientAppInviteCard } from '../../../WellbeingPlan/PatientAppInviteCard';
import CaremapprModal from '../../../Modal/CaremapprModal/CaremapprModal';
import { Form } from 'react-bootstrap';
import CareMapprButton from '../../../Button/CareMapprButton';
import { post } from '../../../../../api/baseUrl';
import { AppFlavour, type InviteToAppPayload } from '../../../../../lib';
import { usePatientProfile } from '../../../ProfileBar/hooks';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../../store';
import Swal from 'sweetalert2';

const OSANavigatorApp = () => {
  const { patientProfile } = usePatientProfile();
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const [sent, setSent] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [phone, setPhone] = useState(patientProfile.phone || '');
  const [loading, setLoading] = useState(false);
  const onHide = () => setShowModal(false);

  const { patientOSANavigatorActive } = usePatientProfile();

  const onSendInvite = async (normalized?: string) => {
    if (!selectedTenancy) return;
    setLoading(true);
    try {
      const requestBody: InviteToAppPayload = {
        access_token: patientProfile.access_token,
        phone: normalized ?? phone,
        tenancyId: selectedTenancy.tenancy_id,
        clinicName: '',
        appFlavour: AppFlavour.OSANavigator,
      };
      const {
        data: { message },
      } = await post('inviteToApp', requestBody);
      if (message === 'Success') {
        setSent(true);
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: 'error',
        text: 'Invite patient failed.',
      });
    } finally {
      setLoading(false);
      onHide();
    }
  };

  return (
    <>
      <div className="d-flex align-items-start justify-content-between px-3 w-100">
        <span
          className="mt-4"
          style={{
            fontSize: '18px',
            fontWeight: '600',
            fontFamily: 'Open Sans, sans-serif',
          }}
        >
          OSA Navigator app
        </span>
        <div className=" w-50">
          <PatientAppInviteCard
            activeTitle={
              patientOSANavigatorActive
                ? 'Patient app active'
                : 'Patient app not active'
            }
            description={
              patientOSANavigatorActive || sent
                ? 'Patient account has been linked with CareMappr OSA Navigator app.'
                : 'To receive data from your patient they must have the CareMappr OSA Navigator app installed. '
            }
            onInvitePatient={() => setShowModal(true)}
            showTextField={false}
            buttonName="Send code to patient"
            direction="row"
            sent={sent}
            showButton={!patientOSANavigatorActive}
          />
        </div>
      </div>
      <CaremapprModal
        id="osaNavigatorAppModal"
        show={showModal}
        onHide={onHide}
        size="lg"
        body={
          <OSAInviteModalBody
            onHide={onHide}
            onSendInvite={onSendInvite}
            loading={loading}
            value={phone}
            onValueChange={setPhone}
          />
        }
        header={<OSAInviteModalHeader onHide={onHide} />}
      />
    </>
  );
};

export default OSANavigatorApp;

const OSAInviteModalHeader = ({ onHide }: { onHide: () => void }) => {
  return (
    <div className="d-flex align-items-center">
      <button
        onClick={() => onHide()}
        className="d-flex align-items-center gap-2"
      >
        <i
          style={{ color: 'rgba(38, 83, 116, 1)' }}
          className="fas fa-arrow-left"
        />
        <span
          style={{
            color: 'rgba(38, 83, 116, 1)',
            textDecoration: 'underline',
          }}
        >
          Back to OSA Navigator App
        </span>
      </button>
    </div>
  );
};

const OSAInviteModalBody = ({
  onHide,
  onSendInvite,
  loading,
  value,
  onValueChange,
}: {
  onHide: () => void;
  onSendInvite: (normalized?: string) => void;
  loading: boolean;
  value: string;
  onValueChange: (value: string) => void;
}) => {
  return (
    <form
      className="gap-2 py-2 px-4"
      onSubmit={(e) => {
        e.preventDefault();
        const formEl = e.currentTarget as HTMLFormElement;
        const inputEl = formEl.querySelector(
          'input[type="tel"]',
        ) as HTMLInputElement | null;

        // Normalize AU mobile: +61xxxxxxxxx -> 0xxxxxxxxx; remove non-digits
        const digits = (value || '').replace(/[^\d]/g, '');
        let normalized = digits;
        if (normalized.startsWith('61')) {
          normalized = '0' + normalized.slice(2);
        } else if (/^4\d{8}$/.test(normalized)) {
          // handle 9-digit mobile without leading 0
          normalized = '0' + normalized;
        }

        // Validate normalized against local mobile pattern 04XXXXXXXX
        const isValid = /^04\d{8}$/.test(normalized);
        if (!isValid) {
          if (inputEl) {
            inputEl.setCustomValidity(
              'Please enter a valid Australian phone number',
            );
            inputEl.reportValidity();
          }
          return;
        }

        // Clear any previous validity message and reflect normalized back to state
        if (inputEl) {
          inputEl.setCustomValidity('');
        }
        onValueChange(normalized);

        onSendInvite(normalized);
      }}
    >
      <div style={{ fontWeight: '600' }}>Phone number</div>
      <Form.Control
        type="tel"
        inputMode="numeric"
        pattern="(04[0-9]{8}|\+614[0-9]{8})"
        maxLength={12}
        required
        placeholder="Enter phone number"
        onInvalid={(e) => {
          e.currentTarget.setCustomValidity(
            'Please enter a valid Australian phone number',
          );
        }}
        onInput={(e) => {
          e.currentTarget.setCustomValidity('');
        }}
        onChange={(e) => {
          onValueChange(e.target.value);
        }}
        disabled={loading}
        value={value}
      />
      <div className="d-flex align-items-center justify-content-end gap-2">
        <CareMapprButton
          onClick={() => onHide()}
          variant="secondary"
          label="Cancel"
          type="button"
          isLoading={loading}
          disabled={loading}
        />
        <CareMapprButton
          type="submit"
          label="Send"
          isLoading={loading}
          disabled={loading}
        />
      </div>
    </form>
  );
};
